import type { PayrollDisplayUnit } from "../../routes/payrollDisplay.js";

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const monthLabel = (value: string) => {
  const [year, month] = value
    .split("-")
    .map((item) => Number.parseInt(item, 10));
  return `${MONTH_NAMES[(month || 1) - 1]} ${year}`;
};

export const shortMonthLabel = (value: string) => {
  const [year, month] = value
    .split("-")
    .map((item) => Number.parseInt(item, 10));
  return `${MONTH_NAMES[(month || 1) - 1]?.slice(0, 3) ?? value} ${year}`;
};

export const MonthYearPicker = ({
  value,
  onChange,
  labelPrefix = "Payroll",
  monthStatuses,
}: {
  value: string;
  onChange: (v: string) => void;
  labelPrefix?: string;
  monthStatuses?: Readonly<Record<string, "open" | "closed">>;
}) => {
  const parts = value.split("-");
  const year = parseInt(parts[0] ?? "0", 10);
  const month = parseInt(parts[1] ?? "1", 10);
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);
  const selectCls =
    "h-11 cursor-pointer rounded-lg border border-border bg-elevated px-3 text-sm text-foreground outline-none transition-colors focus-visible:border-accent focus-visible:ring-2 focus-visible:ring-accent/30";

  return (
    <div className="flex items-center gap-2">
      <select
        aria-label={`${labelPrefix} month`}
        value={month}
        onChange={(e) =>
          onChange(`${year}-${String(e.target.value).padStart(2, "0")}`)
        }
        className={`${selectCls}${monthStatuses ? " w-52 min-w-0" : ""}`}
      >
        {MONTH_NAMES.map((name, i) => {
          const status = monthStatuses?.[`${year}-${String(i + 1).padStart(2, "0")}`];
          const statusLabel = status === "closed" ? "Closed" : status === "open" ? "Open" : "Unknown";
          return (
            <option key={i + 1} value={i + 1}>
              {name}{monthStatuses ? ` · ${statusLabel}` : ""}
            </option>
          );
        })}
      </select>
      <select
        aria-label={`${labelPrefix} year`}
        value={year}
        onChange={(e) =>
          onChange(`${e.target.value}-${String(month).padStart(2, "0")}`)
        }
        className={selectCls}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
};

export const PayrollUnitToggle = ({
  value,
  onChange,
}: {
  value: PayrollDisplayUnit;
  onChange: (value: PayrollDisplayUnit) => void;
}) => (
  <div
    role="group"
    aria-label="Payroll display unit"
    className="inline-flex h-11 items-center rounded-lg border border-border bg-background p-1"
  >
    {(["hours", "days"] as const).map((unit) => (
      <button
        key={unit}
        type="button"
        aria-pressed={value === unit}
        onClick={() => onChange(unit)}
        className={`flex h-9 cursor-pointer items-center rounded-md px-3 text-xs font-semibold outline-none focus-visible:ring-2 focus-visible:ring-accent ${
          value === unit
            ? "bg-elevated text-foreground shadow-sm"
            : "text-foreground-muted"
        }`}
      >
        {unit === "hours" ? "Hours" : "Days"}
      </button>
    ))}
  </div>
);
